import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { ProjectCard, Loader, EmptyState, PageHero } from '../components/Shared';
import ConstructionCanvas from '../components/ConstructionCanvas';

const CRANE = '#d39816';

function CustomSelect({ value, onChange, label, options }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedLabel = options.find(opt => opt.value === value)?.label || label;

  return (
    <div ref={ref} style={{ position: 'relative', width: 'auto' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          padding: '5px 12px',
          fontSize: '0.875rem',
          background: 'rgba(255,255,255,0.85)',
          border: `1px solid rgba(211,152,22,${open ? '0.4' : '0.2'})`,
          borderRadius: 'var(--radius)',
          color: 'var(--text)',
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'var(--transition)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          paddingRight: '12px',
          boxShadow: open ? '0 0 0 3px rgba(211,152,22,0.1)' : 'none',
        }}>
        {selectedLabel}
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: '0.75rem', color: CRANE, marginLeft: 'auto' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          left: 0,
          right: 0,
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(8px)',
          border: `1px solid rgba(211,152,22,0.2)`,
          borderRadius: 'var(--radius)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          zIndex: 1000,
          minWidth: '200px',
          overflow: 'hidden',
        }}>
          {options.map((opt, i) => (
            <button
              key={i}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              style={{
                width: '100%',
                padding: '5px 12px',
                fontSize: '0.875rem',
                background: value === opt.value ? 'rgba(211,152,22,0.08)' : 'transparent',
                border: 'none',
                borderLeft: value === opt.value ? `3px solid ${CRANE}` : '3px solid transparent',
                color: value === opt.value ? CRANE : 'var(--text-2)',
                fontWeight: value === opt.value ? 600 : 500,
                cursor: 'pointer',
                transition: 'var(--transition)',
                textAlign: 'left',
              }}
              onMouseEnter={e => {
                if (value !== opt.value) {
                  e.currentTarget.style.background = 'rgba(211,152,22,0.04)';
                  e.currentTarget.style.color = 'var(--text)';
                }
              }}
              onMouseLeave={e => {
                if (value !== opt.value) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--text-2)';
                }
              }}>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectsPage() {
  const [projects,   setProjects]   = useState([]);
  const [services,   setServices]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [filters,    setFilters]    = useState({ status: '', service: '' });
  const navigate = useNavigate();

  useEffect(() => { api.get('/services').then(r => setServices(r.data.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 9, ...filters });
    Object.keys(filters).forEach(k => !filters[k] && p.delete(k));
    api.get(`/projects?${p}`).then(r => {
      const d = r.data.data;
      setProjects(d.projects || []);
      setTotalPages(d.total_pages || 1);
      setTotal(d.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [page, filters]);

  const handleFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };

  return (
    <>
      <PageHero
        label="Our Portfolio"
        title="Construction"
        accent="Projects"
        subtitle="Explore our landmark projects across residential, commercial, and industrial sectors."
        image="/hero-projects.jpg"
        pageClass="projects"
      />

      {/* Projects Introduction */}
      <section style={{ position: 'relative', background: 'var(--site-bg-2)', padding: '56px 0', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          <ConstructionCanvas variant="blueprint" color={CRANE} opacity={0.3} />
        </div>
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="text-center mb-3">
            <span className="section-label">Landmark Achievements</span>
            <h2 className="section-title dark-text">Featured <span>Projects</span></h2>
            <span className="divider-accent center" />
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <p style={{ color: 'var(--text-2)', fontSize: '1.05rem', lineHeight: 1.9, marginBottom: 16 }}>
                Every project we undertake is a testament to our commitment to excellence, innovation, and craftsmanship. From residential sanctuaries to commercial landmarks, our portfolio showcases the transformative power of visionary design and meticulous execution.
              </p>
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.85, marginBottom: 0 }}>
                Explore the diverse range of projects that demonstrate our expertise across multiple sectors. Each structure reflects our dedication to creating spaces that inspire, endure, and exceed expectations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <div style={{ background: 'var(--site-bg-2)', borderBottom: '1px solid rgba(211,152,22,0.12)', padding: '18px 0', boxShadow: '0 2px 12px rgba(44,40,64,0.06)' }}>
        <div className="container d-flex flex-wrap align-items-center gap-3">
          <CustomSelect
            value={filters.status}
            onChange={v => handleFilter('status', v)}
            label="All Status"
            options={[
              { value: '', label: 'All Status' },
              { value: 'completed', label: 'Completed' },
              { value: 'ongoing', label: 'Ongoing' },
              { value: 'upcoming', label: 'Upcoming' },
            ]}
          />
          <CustomSelect
            value={filters.service}
            onChange={v => handleFilter('service', v)}
            label="All Services"
            options={[{ value: '', label: 'All Services' }, ...services.map(s => ({ value: s.id, label: s.name }))]}
          />
          {(filters.status || filters.service) && (
            <button onClick={() => { setFilters({ status: '', service: '' }); setPage(1); }}
              style={{ padding: '2px 8px', fontSize: '0.875rem', background: 'rgba(211,152,22,0.08)', border: '1px solid rgba(211,152,22,0.2)', borderRadius: 'var(--radius)', color: 'var(--text-2)', fontWeight: 500, cursor: 'pointer', transition: 'var(--transition)', display: 'flex', alignItems: 'center', gap: 6 }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(211,152,22,0.15)'; e.currentTarget.style.borderColor = 'rgba(211,152,22,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(211,152,22,0.08)'; e.currentTarget.style.borderColor = 'rgba(211,152,22,0.2)'; }}>
              <i className="bi bi-x" style={{ fontSize: '1rem' }} />Clear
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      <section style={{ position: 'relative', background: 'var(--site-bg-3)', padding: '50px 0', overflow: 'hidden', minHeight: 500 }}>
        <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          <ConstructionCanvas variant="structure" color={CRANE} opacity={0.35} />
        </div>
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          {loading ? (
            <div className="text-center py-5"><div className="spinner dark mx-auto" /></div>
          ) : projects.length === 0 ? (
            <EmptyState icon="bi-building" title="No projects found" subtitle="Try adjusting your filters." />
          ) : (
            <>
              <div className="row g-4">
                {projects.map((p, i) => (
                  <div key={p.id} className="col-md-6 col-lg-4" style={{ animation: `fadeInUp 0.55s ${0.06 * i}s ease both` }}>
                    <ProjectCard project={p} onClick={() => navigate(`/projects/${p.slug}`)} />
                  </div>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-5">
                  <nav>
                    <ul className="pagination pagination-light">
                      <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(p => p - 1)}><i className="bi bi-chevron-left" /></button>
                      </li>
                      {[...Array(totalPages)].map((_, i) => (
                        <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
                        </li>
                      ))}
                      <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(p => p + 1)}><i className="bi bi-chevron-right" /></button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}
